/**
 * Shared immersive-mode plumbing for app surfaces hosted inside MJExplorer.
 *
 * An "immersive" surface (MAPS, Feedback Platform, Research Accelerator) hides
 * the Explorer shell chrome while it owns the screen. The body class that drives
 * those global CSS rules is DERIVED state, never last-writer-wins, because the
 * Explorer shell is hostile to naive lifecycle assumptions:
 *
 *   - tab switches DETACH a surface without destroying it (component cache);
 *   - duplicate tabs of the same dashboard orphan the cached instance;
 *   - the shell's LRU eviction destroys stale instances long after a fresh one
 *     is already visible;
 *   - `BaseDashboard.SetVisible` has zero callers in MJ 5.51.
 *
 * Any of those produces a LATE signal from a stale instance (an
 * IntersectionObserver `false` on a detached element, a delayed ngOnDestroy).
 * Imperative toggling therefore raced in production (seen deployed 2026-08-18:
 * nav visible over a visible surface after tab re-activation). Every signal
 * instead RECOMPUTES the predicate below, so a late writer self-corrects.
 *
 * This is the single implementation; per-surface hosts bind it to their own
 * element selector and body class via BaseAGUAppSurfaceComponent.
 */

/**
 * Toggle `bodyClass` to exactly match "some connected, non-zero-size element
 * matching `surfaceSelector` is in the document".
 *
 * A detached or `display: none` element measures 0x0, which is what
 * distinguishes a cached-but-hidden surface from the one actually on screen.
 */
export function SyncSurfaceImmersiveClass(
    doc: Document,
    surfaceSelector: string,
    bodyClass: string
): void {
    const anyVisible = Array.from(doc.querySelectorAll(surfaceSelector)).some((el) => {
        if (!el.isConnected) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0; // detached / display:none ⇒ 0×0
    });
    doc.body.classList.toggle(bodyClass, anyVisible);
}
